import { ref } from 'vue'

/**
 * Serialises async work so only one operation runs at a time. While a drain is
 * in flight, further `enqueue` calls replace any pending operation — only the
 * latest intent survives (latest wins).
 */
export function useLatestWinsQueue(onDrainComplete?: () => void) {
  const pending = ref(false)

  let draining: Promise<void> | null = null
  let queuedOperation: (() => Promise<void>) | null = null

  function enqueue(operation: () => Promise<void>): Promise<void> {
    queuedOperation = operation
    if (!draining) {
      draining = drain()
    }
    return draining
  }

  /** Resolves when the current drain cycle finishes (or immediately if idle). */
  function whenIdle(): Promise<void> {
    return draining ?? Promise.resolve()
  }

  async function drain(): Promise<void> {
    pending.value = true
    try {
      while (queuedOperation) {
        const operation = queuedOperation
        queuedOperation = null
        await operation()
      }
    } finally {
      onDrainComplete?.()
      pending.value = false
      draining = null
    }
  }

  return { pending, enqueue, whenIdle }
}
